import { useI18n } from "vue-i18n";
import { usePrimeVue } from "primevue/config";
import LookupListItem from "@/types/lookup/LookupListItem";
import type TranslationListItem from "@/types/translation/TranslationListItem";
import { Info } from "luxon";
import { ref } from "vue";
import { storeToRefs } from "pinia";
import { Settings } from 'luxon';
import { useTranslationStore } from "@/stores/translationStore";

export function useLocaleSettings() {
    const primevue = usePrimeVue();
    const { t, locale } = useI18n({ useScope: 'global' })
    const locales = ref([
        new LookupListItem(1, null, 'en-US', 'EN', 1, true, new Array<TranslationListItem>),
        new LookupListItem(1, null, 'ru-RU', 'RU', 1, true, new Array<TranslationListItem>),
        new LookupListItem(1, null, 'et-EE', 'ET', 1, true, new Array<TranslationListItem>)
    ] as LookupListItem[]);
    const translationStore = useTranslationStore();
    const { currentLocale } = storeToRefs(translationStore);

    function InitLocale(): void {
        ChangeLocale(GetCurrentLocale());
    }

    function ChangeLocale(localeLookup: LookupListItem) {
        localStorage.setItem('locale', localeLookup.code);
        locale.value = localeLookup.code;
        currentLocale.value = localeLookup.code;
        Settings.defaultLocale = currentLocale.value;
        SetLocaleSettings();
    }

    function SetLocaleSettings(): void {
        if (!primevue.config.locale) return;

        primevue.config.locale.monthNames = Info.months("long", { locale: locale.value });
        primevue.config.locale.monthNamesShort = Info.months("short", { locale: locale.value });
        
        // Luxon returns weekdays in ISO order (Mon-Sun), but PrimeVue expects Sun-Mon-Sat
        // Reorder: move Sunday (last item) to the beginning
        const weekdaysLong = Info.weekdays("long", { locale: locale.value });
        const weekdaysNarrow = Info.weekdays("narrow", { locale: locale.value });
        const weekdaysShort = Info.weekdays("short", { locale: locale.value });
        
        primevue.config.locale.dayNames = [weekdaysLong[6], ...weekdaysLong.slice(0, 6)];
        primevue.config.locale.dayNamesShort = [weekdaysNarrow[6], ...weekdaysNarrow.slice(0, 6)];
        primevue.config.locale.dayNamesMin = [weekdaysShort[6], ...weekdaysShort.slice(0, 6)];
        
        // Luxon returns 1-7 (Mon=1, Sun=7), PrimeVue expects 0-6 (Sun=0, Mon=1)
        // Convert: 7→0, keep 1-6 as is
        const luxonFirstDay = Info.getStartOfWeek({ locale: locale.value });
        primevue.config.locale.firstDayOfWeek = luxonFirstDay % 7;
        
        primevue.config.locale.today = t('common.Today');
        primevue.config.locale.clear = t('common.Clear');
        primevue.config.locale.dateFormat = getDateFormat();
    }
    function getDateFormat(): string {
        const formatter = new Intl.DateTimeFormat(locale.value, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
        const parts = formatter.formatToParts(new Date());
        const format = parts.map((part) => {
            switch (part.type) {
                case "day":
                    return "dd";
                case "month":
                    return "mm";
                case "year":
                    return "yy";
                default:
                    return part.value;
            }
        }).join("");
        return format;
    }

    function GetLocales(): Array<LookupListItem> {
        return locales.value
    }

    function GetCurrentLocale(): LookupListItem {
        let initialLocale = locales.value.find(x => x.code === locale.value) ?? new LookupListItem(1, null, 'en-US', 'EN', 1, true, new Array<TranslationListItem>);
        const storedLang = localStorage.getItem('locale');
        if (storedLang)
            initialLocale = locales.value.find(x => x.code === storedLang) ?? new LookupListItem(1, null, 'en-US', 'EN', 1, true, new Array<TranslationListItem>);

        return initialLocale
    }

    return {
        InitLocale,
        ChangeLocale,
        GetLocales,
        GetCurrentLocale
    };
}